import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Grid, Typography } from "@mui/material"
import fp from "lodash/fp"

import type { InstallationType, InstallationDetailsForm } from "types/device.types"
import SkeletonWrapper from "widgets/common/SkeletonWrapper"
import { DETAILS_CONFIG } from "./config"

interface InstallationDetailsViewProps {
  installationType: InstallationType
  values: InstallationDetailsForm
  isLoading: boolean
}

const InstallationDetailsView = ({
  installationType,
  values,
  isLoading,
}: InstallationDetailsViewProps) => {
  const { t, i18n } = useTranslation()

  const fields = useMemo(() => {
    const config = installationType ? DETAILS_CONFIG[installationType] : []
    return config.map((field) => {
      const value = values[field.name]

      const displayValue =
        value === "" || fp.isNil(value)
          ? "-"
          : field.isTranslatable
            ? t(`device_installation.${String(value).toUpperCase()}`)
            : field.type === "number" && typeof value === "number"
              ? new Intl.NumberFormat(i18n.language).format(value)
              : value

      return (
        <Grid container columnSpacing={4} mb={2} alignItems="center" key={field.name}>
          <Grid item xs={12} sm={6}>
            <Typography>{t(field.labelKey, field.labelOptions)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SkeletonWrapper isWaiting={isLoading} width={150}>
              <Typography color="text.disabled" overflow="hidden" textOverflow="ellipsis">
                {displayValue}
              </Typography>
            </SkeletonWrapper>
          </Grid>
        </Grid>
      )
    })
  }, [installationType, values, isLoading, t, i18n.language])

  const FIELD_SPLIT_THRESHOLD = 6
  const MAX_FIELDS_IN_MIN_LAYOUT = FIELD_SPLIT_THRESHOLD * 2

  if (fields.length > FIELD_SPLIT_THRESHOLD) {
    const midPoint =
      fields.length > MAX_FIELDS_IN_MIN_LAYOUT
        ? Math.ceil(fields.length / 2)
        : FIELD_SPLIT_THRESHOLD

    const columnOneFields = fields.slice(0, midPoint)
    const columnTwoFields = fields.slice(midPoint)

    return (
      <Grid container spacing={{ xs: 0, md: 4 }}>
        <Grid item xs={12} md={6}>
          {columnOneFields}
        </Grid>
        <Grid item xs={12} md={6}>
          {columnTwoFields}
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container>
      <Grid item xs={12} md={6}>
        {fields}
      </Grid>
    </Grid>
  )
}

export default InstallationDetailsView
